import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout, DocsCallout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

export default function AtlasKnowledge() {
  return (
    <>
      <NextSeo
        title="Atlas knowledge and answer quality | Chat.co"
        description="Prepare sources, refresh knowledge when available and read quality evidence."
      />
      <DocsArticleLayout
        category="Atlas"
        categoryColor="text-blue-700"
        title="Atlas knowledge and answer quality"
        description="Treat source processing and answer quality as separate checks."
        readingTime="5 min read"
        tableOfContents={[
          { id: 'sources', title: 'Prepare useful sources' },
          { id: 'onboarding', title: 'Onboarding when available' },
          { id: 'refresh', title: 'Refresh when available' },
          { id: 'quality', title: 'Read test and citation evidence' },
        ]}
        prevArticle={{ title: 'Review and Apply in Atlas', href: PageLink.docsAtlasApprovals }}
        nextArticle={{
          title: 'Atlas roles, privacy and key delivery',
          href: PageLink.docsAtlasPrivacy,
        }}>
        <section id="sources">
          <h2>Prepare useful sources</h2>
          <p>
            Choose the correct bot and inspect its current sources before adding content. Use
            authoritative documents, a relevant website, concise text or Q&amp;A pairs. Remove
            obsolete or contradictory material and decide which facts the bot should be able to
            answer.
          </p>
          <p>
            Keep credentials and unnecessary personal data out of training content. The{' '}
            <Link href={PageLink.docsKnowledgeUpload}>knowledge upload playbook</Link> explains
            source selection and the existing classic upload screens.
          </p>
        </section>
        <section id="onboarding">
          <h2>Onboarding when available</h2>
          <DocsCallout title="Check capability availability">
            Atlas access is available to enrolled accounts with the required permissions. Automated
            knowledge onboarding and refresh require separate enablement and may be unavailable in
            your workspace.
          </DocsCallout>
          <p>
            If onboarding is offered, provide the website or supported attachment requested by the
            app. Read the proposed bot setup, selected content and sample questions. Each
            consequential action needs its own review card and an explicit click on its approval
            button. Use Apply on a hosted workflow review or Approve and its send, publish or delete
            variants on a standard review. An ordinary chat yes is not approval.
          </p>
          <p>
            Bot creation, source changes, training and paid tests are separate decisions.
            Informational plans have no blanket Apply. If onboarding is unavailable, use Classic to
            create the bot and add supported sources; do not assume that a chat request has started
            an automated workflow.
          </p>
        </section>
        <section id="refresh">
          <h2>Refresh when available</h2>
          <p>
            For an enabled refresh workflow, identify the existing bot and website. Inspect the
            proposed changes and selected pages.
          </p>
          <p>
            Pages of unknown freshness are not refreshed unless you include them. Where Edit is
            offered, it can include only listed pages of unknown freshness. It cannot remove new or
            changed pages. If the fixed selection is wrong, do not Apply it: Dismiss stops that
            refresh, or request a revised proposal. Apply only the exact reviewed effect. Review
            later retraining and testing separately.
          </p>
          <p>
            After processing, check the source status and test results. A completed crawl or upload
            does not establish that every answer is accurate. It does not prove that an expert
            knowledge base has finished updating. If the task is interrupted or synchronization is
            incomplete, check its evidence before deciding what to do next.
          </p>
        </section>
        <section id="quality">
          <h2>Read test and citation evidence</h2>
          <p>
            Prepare representative questions with expected behavior: a correct fact, a question
            requiring a citation, and a question the bot should decline because the source does not
            support it. Where Atlas offers expert test questions, a reviewed test can run up to five
            questions. Provider cost may be unknown in advance.
          </p>
          <p>
            A successful invocation means the expert returned an answer. A quality pass is a
            separate assessment. Read each answer, expected behavior, verdict and reasons. Missing
            evidence stays unknown; an answer without enough evidence is not a verified pass.
          </p>
          <p>
            Check citations against the actual document and relevant passage. A link alone does not
            prove the answer is supported. See{' '}
            <Link href={PageLink.docsCitationQuality}>citation quality guidance</Link>. Record which
            sources and time period support your conclusion, and test again after a reviewed content
            change when appropriate.
          </p>
          <p>
            If an earlier paid test may have run but its result is missing, follow{' '}
            <Link href={PageLink.docsAtlasTroubleshooting}>unknown outcome guidance</Link> before
            requesting another invocation.
          </p>
        </section>
      </DocsArticleLayout>
    </>
  );
}
